import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveLeaveRequest,
  createLeaveRequest,
  deleteLeaveRequest,
  fetchLeaveRequest,
  fetchLeaveRequests,
  fetchMyLeaveBalance,
  revokeLeaveRequest,
  updateLeaveRequest,
  downloadLeaveRequestPdf,
} from '../../api/leaveRequest';
import { APPROVAL_TARGET_LEAVE } from '../../types/approval';
import {
  HalfDayPeriod,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
  LEAVE_TYPE_OPTIONS,
  consumesLeaveBalance,
  formatLeaveDays,
  halfDayPeriodLabel,
  leaveStatusClass,
  leaveStatusLabel,
  leaveTypeLabel,
} from '../../types/leaveRequest';
import {notifyError, reportError} from '../../utils/notify';
import { useCompanyStore } from '../../store/companyStore';
import { canConfirmInspection } from '../../types/companyRole';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import ListPager from '../common/ListPager';

import './LeaveRequestPanel.css';

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; leaveRequestId: number }
  | { type: 'edit'; leaveRequestId: number };

const LIST_VIEW = { type: 'list' } as const;

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function calcDays(leaveType: string, startDate: string, endDate: string) {
  if (!startDate || !endDate) return '';
  if (leaveType === 'HALF') return '0.5';
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return '';
  const diff = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  return String(diff);
}

function LeaveRequestPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveAsManager = canConfirmInspection(role);
  const currentYear = new Date().getFullYear();
  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [detail, setDetail] = useState<LeaveRequest | null>(null);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [leaveType, setLeaveType] = useState<LeaveType>('ANNUAL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState('');
  const [halfDayPeriod, setHalfDayPeriod] = useState<HalfDayPeriod>('AM');
  const [reason, setReason] = useState('');

  const suggestedDays = useMemo(
    () => calcDays(leaveType, startDate, endDate),
    [leaveType, startDate, endDate]
  );

  const leaveYear = useMemo(() => {
    if (startDate && /^\d{4}/.test(startDate)) return Number(startDate.slice(0, 4));
    return currentYear;
  }, [startDate, currentYear]);

  const remaining = balance?.remainingDays ?? 0;
  const requestDays = Number(days) || 0;
  const willConsume = consumesLeaveBalance(leaveType);
  const insufficient =
    willConsume && requestDays > 0 && remaining + 1e-9 < requestDays;

  const loadBalance = useCallback(async (year?: number) => {
    try {
      setBalance(await fetchMyLeaveBalance(year ?? currentYear));
    } catch {
      setBalance(null);
    }
  }, [currentYear]);

  const loadList = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const [result] = await Promise.all([
        fetchLeaveRequests({ page: pageNum }),
        loadBalance(currentYear),
      ]);
      setRows(result.content || []);
      setPage(result.page ?? pageNum);
      setTotalPages(result.totalPages ?? 0);
      setTotalElements(result.totalElements ?? 0);
      setError('');
    } catch {
      reportError(setError, '휴가 신청 목록을 불러오지 못했습니다.');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [loadBalance, currentYear]);

  useEffect(() => {
    setView(LIST_VIEW);
    void loadList(0);
  }, [companyId, loadList, setView]);

  useEffect(() => {
    if (view.type === 'create' || view.type === 'edit') {
      void loadBalance(leaveYear);
    }
  }, [view.type, leaveYear, loadBalance]);

  useEffect(() => {
    if (leaveType === 'HALF') {
      setDays('0.5');
      if (startDate) setEndDate(startDate);
      return;
    }
    if (suggestedDays) setDays(suggestedDays);
  }, [leaveType, suggestedDays, startDate]);

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setLeaveType('ANNUAL');
    setStartDate(today);
    setEndDate(today);
    setDays('1');
    setHalfDayPeriod('AM');
    setReason('');
    setDetail(null);
    setView({ type: 'create' });
  };

  const openDetail = async (leaveRequestId: number) => {
    try {
      setLoading(true);
      const row = await fetchLeaveRequest(leaveRequestId);
      setDetail(row);
      setView({ type: 'detail', leaveRequestId });
      setError('');
      const year = row.startDate ? Number(String(row.startDate).slice(0, 4)) : currentYear;
      void loadBalance(year);
    } catch {
      notifyError('휴가 신청을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row: LeaveRequest) => {
    setLeaveType((row.leaveType as LeaveType) || 'ANNUAL');
    setStartDate(formatDate(row.startDate) === '-' ? '' : formatDate(row.startDate));
    setEndDate(formatDate(row.endDate) === '-' ? '' : formatDate(row.endDate));
    setDays(row.days != null ? String(row.days) : '');
    setHalfDayPeriod((row.halfDayPeriod as HalfDayPeriod) || 'AM');
    setReason(row.reason || '');
    setDetail(row);
    setView({ type: 'edit', leaveRequestId: row.leaveRequestId });
  };

  const backToList = () => {
    setView(LIST_VIEW);
    setDetail(null);
    setApprovalOpen(false);
    void loadList(page);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      notifyError('시작일과 종료일을 입력해 주세요.');
      return;
    }
    if (endDate < startDate) {
      notifyError('종료일은 시작일 이후여야 합니다.');
      return;
    }
    const daysNum = Number(days);
    if (!Number.isFinite(daysNum) || daysNum <= 0) {
      notifyError('사용 일수를 확인해 주세요.');
      return;
    }
    if (insufficient) {
      const ok = window.confirm(
        `${leaveYear}년 잔여 연차(${formatLeaveDays(remaining)}일)가 부족합니다.\n작성은 가능하지만 승인 시 잔여가 있어야 합니다. 계속할까요?`
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      const payload = {
        leaveType,
        startDate,
        endDate: leaveType === 'HALF' ? startDate : endDate,
        days: daysNum,
        halfDayPeriod: leaveType === 'HALF' ? halfDayPeriod : undefined,
        reason: reason.trim() || undefined,
      };
      const saved =
        view.type === 'edit'
          ? await updateLeaveRequest(view.leaveRequestId, payload)
          : await createLeaveRequest(payload);
      setDetail(saved);
      setView({ type: 'detail', leaveRequestId: saved.leaveRequestId });
      void loadBalance(leaveYear);
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: LeaveRequest) => {
    if (!window.confirm('이 휴가 신청을 삭제할까요?')) return;
    try {
      await deleteLeaveRequest(row.leaveRequestId);
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '삭제에 실패했습니다.'
      );
    }
  };

  const handleApproveDirect = async () => {
    if (!detail) return;
    if (!window.confirm('바로 승인할까요? 연차·반차는 잔여에서 차감됩니다.')) {
      return;
    }
    try {
      setApproving(true);
      setDetail(await approveLeaveRequest(detail.leaveRequestId));
      void loadBalance(
        detail.startDate ? Number(String(detail.startDate).slice(0, 4)) : currentYear
      );
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail?.canRevoke) return;
    if (!window.confirm('승인을 철회할까요? 차감된 연차는 복구됩니다.')) return;
    try {
      setApproving(true);
      setDetail(await revokeLeaveRequest(detail.leaveRequestId));
      void loadBalance(
        detail.startDate ? Number(String(detail.startDate).slice(0, 4)) : currentYear
      );
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = `${detail.leaveType || 'leave'}_${detail.startDate || detail.leaveRequestId}`;
      await downloadLeaveRequestPdf(detail.leaveRequestId, `연차신청서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderEditActions = () => (
    <div className="leave-panel__form-actions">
      <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
        취소
      </button>
      <button type="submit" className="leave-panel__btn is-primary" disabled={saving}>
        {saving ? '저장 중...' : view.type === 'edit' ? '수정 저장' : '등록'}
      </button>
    </div>
  );

  const renderDetailActions = () => {
    if (!detail) return null;
    return (
      <div className="leave-panel__detail-actions">
        {detail.status === 'APPROVED' ? (
          <button
            type="button"
            className="leave-panel__btn is-primary"
            disabled={pdfDownloading}
            onClick={() => void handleDownloadPdf()}
          >
            {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
          </button>
        ) : null}
        {detail.canRevoke ? (
          <button
            type="button"
            className="leave-panel__btn is-danger"
            disabled={approving}
            onClick={() => void handleRevoke()}
          >
            {approving ? '철회 중...' : '승인 철회'}
          </button>
        ) : null}
        <ApprovalCancelButton
          detail={detail}
          className="leave-panel__btn is-ghost"
          disabled={approving}
          onCancelled={() => void openDetail(detail.leaveRequestId)}
        />
        {detail.canRequestApproval ? (
          <button
            type="button"
            className="leave-panel__btn is-ghost"
            onClick={() => setApprovalOpen(true)}
          >
            결재 요청
          </button>
        ) : null}
        {detail.canApproveDirect && canApproveAsManager ? (
          <button
            type="button"
            className="leave-panel__btn is-primary"
            disabled={approving}
            onClick={() => void handleApproveDirect()}
          >
            {approving ? '승인 중...' : '승인'}
          </button>
        ) : null}
        {detail.canEdit ? (
          <button
            type="button"
            className="leave-panel__btn is-ghost"
            onClick={() => openEdit(detail)}
          >
            수정
          </button>
        ) : null}
        {detail.canDelete ? (
          <button
            type="button"
            className="leave-panel__btn is-danger"
            onClick={() => void handleDelete(detail)}
          >
            삭제
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <main className="leave-panel">
      <header className="leave-panel__head">
        <div>
          <p className="leave-panel__eyebrow">My Leave</p>
          <h2>휴가·출장</h2>
          <p className="leave-panel__guide">
            연차·반차는 승인 시 잔여에서 차감됩니다. 작성중은 본인만, 승인 건은 회사 전원에게
            보입니다.
          </p>
        </div>
        <div className="leave-panel__head-actions">
          {view.type === 'list' ? (
            <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
              신청하기
            </button>
          ) : (
            <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
              목록으로
            </button>
          )}
        </div>
      </header>

      {balance ? (
        <section className="leave-panel__balance">
          <div>
            <strong>{balance.leaveYear}년 연차</strong>
            <p>
              부여 {formatLeaveDays(balance.grantedDays)}
              {' · '}사용 {formatLeaveDays(balance.usedDays)}
            </p>
          </div>
          <div className="leave-panel__balance-remain">
            <span>잔여</span>
            <em>{formatLeaveDays(balance.remainingDays)}일</em>
          </div>
        </section>
      ) : null}

      {error ? <p className="leave-panel__error">{error}</p> : null}

      {view.type === 'list' ? (
        <div className="leave-panel__body">
          {loading ? (
            <p className="leave-panel__hint">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <div className="leave-panel__empty">
              <strong>신청 내역이 없습니다</strong>
              <p>연차·반차·병가·출장 등을 신청해 보세요.</p>
              <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
                신청하기
              </button>
            </div>
          ) : (
            <div className="paged-list">
              <div className="paged-list__scroll">
                <table className="leave-panel__table">
                  <thead>
                    <tr>
                      <th style={{ width: '90px' }}>유형</th>
                      <th>기간</th>
                      <th style={{ width: '90px' }}>일수</th>
                      <th style={{ width: '120px' }}>신청자</th>
                      <th style={{ width: '90px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.leaveRequestId} onClick={() => void openDetail(row.leaveRequestId)}>
                        <td>
                          {leaveTypeLabel(row.leaveType)}
                          {row.leaveType === 'HALF'
                            ? ` (${halfDayPeriodLabel(row.halfDayPeriod)})`
                            : ''}
                        </td>
                        <td className="is-title">
                          {formatDate(row.startDate)} ~ {formatDate(row.endDate)}
                        </td>
                        <td>{row.days}</td>
                        <td>{row.requesterName || '-'}</td>
                        <td>
                          <span
                            className={`leave-panel__status is-${leaveStatusClass(
                              row.status,
                              row.approvalInProgress
                            )}`}
                          >
                            {leaveStatusLabel(row.status, row.approvalInProgress)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                loading={loading}
                onChange={(next) => void loadList(next)}
              />
            </div>
          )}
        </div>
      ) : null}

      {(view.type === 'create' || view.type === 'edit') && (
        <form className="leave-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          {renderEditActions()}
          <label className="leave-panel__field">
            <span>휴가 유형</span>
            <select value={leaveType} onChange={(e) => setLeaveType(e.target.value as LeaveType)}>
              {LEAVE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="leave-panel__row">
            <label className="leave-panel__field">
              <span>시작일</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (leaveType === 'HALF') setEndDate(e.target.value);
                }}
                required
              />
            </label>
            <label className="leave-panel__field">
              <span>종료일</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={leaveType === 'HALF'}
                required
              />
            </label>
            <label className="leave-panel__field">
              <span>사용 일수</span>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                disabled={leaveType === 'HALF'}
                required
              />
            </label>
          </div>
          {leaveType === 'HALF' ? (
            <label className="leave-panel__field">
              <span>반차 구분</span>
              <select
                value={halfDayPeriod}
                onChange={(e) => setHalfDayPeriod(e.target.value as HalfDayPeriod)}
              >
                <option value="AM">오전</option>
                <option value="PM">오후</option>
              </select>
            </label>
          ) : null}
          {willConsume ? (
            <p className={`leave-panel__balance-hint ${insufficient ? 'is-warn' : ''}`}>
              {leaveYear}년 잔여 {formatLeaveDays(remaining)}일
              {insufficient
                ? ' · 잔여가 부족합니다. 승인 시 반려될 수 있으니 연차 부여를 먼저 요청하세요.'
                : ' · 승인되면 이 일수만큼 차감됩니다.'}
            </p>
          ) : (
            <p className="leave-panel__balance-hint">병가·출장·기타는 연차 잔여에서 차감되지 않습니다.</p>
          )}
          <label className="leave-panel__field">
            <span>사유</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="휴가 사유를 입력하세요 (선택)"
            />
          </label>
          {renderEditActions()}
        </form>
      )}

      {view.type === 'detail' && detail ? (
        <article className="leave-panel__detail">
          <div className="leave-panel__detail-head">
            <div>
              <span
                className={`leave-panel__status is-${leaveStatusClass(
                  detail.status,
                  detail.approvalInProgress
                )}`}
              >
                {leaveStatusLabel(detail.status, detail.approvalInProgress)}
              </span>
              <h3>
                {leaveTypeLabel(detail.leaveType)}
                {detail.leaveType === 'HALF'
                  ? ` (${halfDayPeriodLabel(detail.halfDayPeriod)})`
                  : ''}{' '}
                · {formatDate(detail.startDate)} ~ {formatDate(detail.endDate)}
              </h3>
              <p>
                {detail.requesterName || '-'} · {detail.days}일
                {detail.approverName ? ` · 승인자 ${detail.approverName}` : ''}
                {detail.balanceApplied ? ' · 연차 차감됨' : ''}
              </p>
              {detail.approvalInProgress ? (
                <p className="leave-panel__hint" style={{ marginTop: 8 }}>
                  결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
                </p>
              ) : null}
            </div>
            {renderDetailActions()}
          </div>
          <dl className="leave-panel__meta">
            <div>
              <dt>유형</dt>
              <dd>
                {leaveTypeLabel(detail.leaveType)}
                {detail.leaveType === 'HALF'
                  ? ` · ${halfDayPeriodLabel(detail.halfDayPeriod)}`
                  : ''}
              </dd>
            </div>
            <div>
              <dt>기간</dt>
              <dd>
                {formatDate(detail.startDate)} ~ {formatDate(detail.endDate)}
              </dd>
            </div>
            <div>
              <dt>일수</dt>
              <dd>{detail.days}</dd>
            </div>
            <div>
              <dt>사유</dt>
              <dd className="is-reason">{detail.reason || '-'}</dd>
            </div>
          </dl>

          {renderDetailActions()}
          {detail.canRequestApproval || detail.canApproveDirect ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${leaveTypeLabel(detail.leaveType)} ${formatDate(detail.startDate)}~${formatDate(detail.endDate)} 결재`}
              targetType={APPROVAL_TARGET_LEAVE}
              targetId={detail.leaveRequestId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                void openDetail(detail.leaveRequestId);
                void loadBalance(currentYear);
              }}
            />
          ) : null}
        </article>
      ) : null}
    </main>
  );
}

export default LeaveRequestPanel;
